import { useState } from "react";
import DropDown from "../drop-down/drop-down";
import { DropdownItemSelectedParams } from "../drop-down/DropdownItemSelectedParams";
import { SearchBarSearchParams } from "./SearchBarSearchParams";
import { useTranslation } from "react-i18next";
import KaribuSvgIcon from "../karibu-svg-icon/karibu-svg-icon";

type Props = {
  searchList?: string[];
  onSearch: (searchParams: SearchBarSearchParams) => void;
  onTextChanged?: (searchParams: SearchBarSearchParams) => void;
  hideDropdown?: boolean;
  value?: string;
  initialSelectedDropdownIndex?: number;
  compact?: boolean;
};

const SearchBar = ({
  searchList,
  value,
  initialSelectedDropdownIndex,
  onSearch,
  onTextChanged,
  hideDropdown,
  compact = false,
}: Props) => {
  const { t } = useTranslation();
  const [selectedDropDownValue, setSelectedDropdownValue] = useState("");
  const [searchText, setSearchText] = useState(value ?? "");

  const onDropdownItemSelected = (
    itemSelectedParams: DropdownItemSelectedParams
  ) => {
    setSelectedDropdownValue(itemSelectedParams.selectedItem);
  };

  const textChanged = (evt) => {
    setSearchText(evt.target.value);

    if (onTextChanged)
      onTextChanged({
        text: evt.target.value,
        selectedSearchItem: selectedDropDownValue,
        eventParams: evt,
      });
  };

  const searchClicked = (evt) => {
    onSearch({
      text: searchText,
      selectedSearchItem: selectedDropDownValue,
      eventParams: evt,
    });
  };

  const searchKeyPressed = (evt) => {
    if (evt.key === "Enter") {
      searchClicked(evt);
    }
  };

  // Logistics mock: compact search input (no dropdown, icon inside input)
  if (compact && hideDropdown) {
    return (
      <div className="relative w-full">
        <input
          value={searchText}
          onChange={textChanged}
          onKeyPress={searchKeyPressed}
          className="block h-10 w-full rounded-lg border border-gray-300 bg-white px-3 pr-9 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none"
          placeholder={value?.trim() ? value : `${t("search-bar.search")}`}
          type="text"
          style={{ caretColor: "rgb(107, 114, 128)" }}
        />
        <div
          className="absolute inset-y-0 right-0 flex items-center pr-3"
          onClick={searchClicked}
        >
          <KaribuSvgIcon
            name="search"
            className="cursor-pointer w-4 h-4 fill-slate-500"
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="search-bar flex items-center w-full"
        style={{
          zIndex: 20,
        }}
      >
        <div className="relative w-full max-w-full transform transition-all opacity-100 scale-100">
          <div
            className={
              compact
                ? "rounded-lg bg-white border border-gray-300"
                : "rounded-lg bg-white border-gray-200 border-2"
            }
            id="headlessui-dialog-panel-35"
          >
            <div
              className={
                compact
                  ? "relative flex items-center min-h-[1.4375em] h-10"
                  : "relative flex items-center min-h-[1.4375em]"
              }
            >
              <DropDown
                className={hideDropdown ? "hidden" : ""}
                searchList={searchList ?? []}
                initialSelectedIndex={initialSelectedDropdownIndex}
                onItemSelected={onDropdownItemSelected}
              />

              <div
                className={`seperator bg-gray-200 ${compact ? "h-10" : "h-12"} mx-2 ${
                  hideDropdown ? "hidden" : ""
                }`}
                style={{ width: "1px" }}
              ></div>

              <input
                value={searchText}
                onChange={textChanged}
                onKeyUp={(evt) => {
                  if (evt.code === "Enter") {
                    searchClicked(evt);
                  }
                }}
                className={
                  compact
                    ? "block flex-grow text-ellipsis appearance-none bg-transparent pl-2 text-sm placeholder:text-sm text-slate-900 placeholder:text-slate-600 focus:outline-none"
                    : "block flex-grow text-ellipsis appearance-none bg-transparent pl-2 lg:text-[1rem] placeholder:text-[1rem] text-slate-900 placeholder:text-slate-600 focus:outline-none sm:text-sm sm:leading-6"
                }
                placeholder={
                  selectedDropDownValue
                    ? `${t("search-bar.search-by")} ${t(selectedDropDownValue)}`
                    : value?.trim()?value:`${t("search-bar.search")}`
                }
                id="headlessui-combobox-input-36"
                type="text"
                style={{ caretColor: "rgb(107, 114, 128)" }}
              />

              <div
                className={
                  compact
                    ? "search-icon h-10 flex items-center justify-center px-3"
                    : "search-icon h-12 flex items-center justify-center p-4"
                }
                onClick={searchClicked}
              >
                <KaribuSvgIcon
                        name="search"
                        className={
                          compact
                            ? "cursor-pointer w-5 h-5 fill-slate-400"
                            : "cursor-pointer w-8 h-8 fill-slate-400"
                        }
                      />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchBar;
